/**
 * Forgot Password Component
 * Handles password reset request flow
 */

import React, { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Icon,
  Image,
  Link,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { FaArrowLeft } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { isValidEmail, maskEmail } from '../utils/security.utils';

interface ForgotPasswordProps {}

const ForgotPassword: React.FC<ForgotPasswordProps> = () => {
  const toast = useToast();
  const { requestPasswordReset, isLoading } = useAuth();
  const { t } = useLanguage();

  // Form state
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [submitted, setSubmitted] = useState(false);

  /**
   * Clear errors when user starts typing
   */
  React.useEffect(() => {
    if (email) {
      setErrors({});
    }
  }, [email]);

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: { email?: string } = {};

    if (!email.trim()) {
      newErrors.email = t('forgot.email_required');
    } else if (!isValidEmail(email)) {
      newErrors.email = t('forgot.email_invalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle password reset request
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const response = await requestPasswordReset(email);

      if (response.success) {
        setSubmitted(true);
        toast({
          title: t('forgot.toast_success'),
          description: response.message,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('forgot.toast_failed'),
          description: response.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      toast({
        title: t('common.error'),
        description: err.message || t('common.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit(e as any);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="appleBg"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Box maxW="360px" w="full" py={20}>
        <VStack spacing={8}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Image src="/BRL_Blue_Squared.png" alt="Logo" boxSize={10} />
            <Heading fontSize="28px" fontWeight="600" lineHeight="1.14" color="appleTextPrimary">
              {t('forgot.title')}
            </Heading>
            <Text color="appleTextSecondary" fontSize="sm">
              {submitted
                ? t('forgot.subtitle_check')
                : t('forgot.subtitle_enter')}
            </Text>
          </VStack>

          {!submitted ? (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <VStack spacing={6} w="full">
                {/* Info Alert */}
                <Alert status="info" variant="subtle" borderRadius="sm" w="full">
                  <AlertIcon />
                  <Box>
                    <AlertDescription fontSize="sm">
                      {t('forgot.alert_info')}
                    </AlertDescription>
                  </Box>
                </Alert>

                {/* Email */}
                <FormControl isInvalid={!!errors.email} isRequired>
                  <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                    {t('forgot.email_label')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      variant="flushed"
                      type="email"
                      placeholder={t('forgot.email_placeholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      onKeyPress={handleKeyPress}
                      size="lg"
                      autoComplete="email"
                      aria-label="Email address"
                      focusBorderColor="appleAccent"
                      borderColor="appleCardBorder"
                    />
                  </InputGroup>
                  <FormHelperText fontSize="xs">
                    {t('forgot.email_helper')}
                  </FormHelperText>
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  borderRadius="full"
                  isLoading={isLoading}
                  loadingText={t('forgot.sending')}
                >
                  {t('forgot.send_btn')}
                </Button>

                {/* Back to Login Link */}
                <Link
                  as={RouterLink}
                  to="/login"
                  fontSize="sm"
                  color="appleAccent"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                >
                  <Icon as={FaArrowLeft} />
                  {t('forgot.back_login')}
                </Link>
              </VStack>
            </form>
          ) : (
            <VStack spacing={6} w="full">
              {/* Success Message */}
              <Alert status="success" variant="subtle" borderRadius="sm" w="full">
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('forgot.success_title')}</AlertTitle>
                  <AlertDescription>
                    {t('forgot.success_desc', { email: maskEmail(email) })}
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Next Steps */}
              <Box
                bg="appleSurface"
                p={4}
                borderRadius="sm"
                w="full"
                borderWidth="1px"
                borderColor="appleCardBorder"
              >
                <Text fontSize="sm" color="appleTextPrimary" fontWeight="bold" mb={2}>
                  {t('forgot.next_steps')}
                </Text>
                <VStack align="flex-start" spacing={2}>
                  <Text fontSize="xs" color="appleTextSecondary">
                    {t('forgot.step_1')}
                  </Text>
                  <Text fontSize="xs" color="appleTextSecondary">
                    {t('forgot.step_2')}
                  </Text>
                  <Text fontSize="xs" color="appleTextSecondary">
                    {t('forgot.step_3')}
                  </Text>
                  <Text fontSize="xs" color="appleTextSecondary">
                    {t('forgot.step_4')}
                  </Text>
                </VStack>
              </Box>

              {/* Actions */}
              <VStack spacing={3} w="full">
                <Button
                  colorScheme="blue"
                  size="md"
                  w="full"
                  borderRadius="full"
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                >
                  {t('forgot.send_again')}
                </Button>

                <Link
                  as={RouterLink}
                  to="/login"
                  fontSize="sm"
                  color="appleAccent"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                >
                  <Icon as={FaArrowLeft} />
                  {t('forgot.back_login')}
                </Link>
              </VStack>
            </VStack>
          )}

          {/* Security Note */}
          <Box
            bg="appleSurface"
            p={3}
            borderRadius="sm"
            w="full"
            borderWidth="1px"
            borderColor="appleCardBorder"
          >
            <Text fontSize="xs" color="appleTextSecondary">
              <strong>{t('common.info')}:</strong> {t('forgot.security_note')}
            </Text>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
